import React from 'react';
import { Calendar } from '@/components/ui/calendar';
import { DateTime } from 'luxon';
import { DATESTR_FMT, TDR } from 'tdr-common';
import { useTranslation } from 'react-i18next';
import { resolveHorizonDate } from '@/helpers/horizon';
import { fromLocalJsDate, toLocalJsDate } from '../../calendar/Calendar.utils';
import { cn } from '@/lib/utils';
import { analytics, FILTER_CHANGED } from '../../../helpers/analytics';
import { useFilterAnalytics } from '../../../helpers/analytics/filterContext';

export interface MobileOpenCalendarProps {
	restaurant: TDR.Restaurant;
	value?: string;
	onChange: (dateStr: string) => void;
}

export const MobileOpenCalendar = ({ restaurant, value, onChange }: MobileOpenCalendarProps) => {
	const { i18n } = useTranslation();
	const filterCtx = useFilterAnalytics('global explore');
	const zone = restaurant?.timezone ?? 'UTC';
	const today = DateTime.now().setZone(zone);

	const { date: horizonDate } = resolveHorizonDate({
		policies: undefined,
		restaurant,
		now: today
	});

	const todayStart = today.startOf('day');

	const horizonEnd = horizonDate.startOf('day');

	const months: Date[] = [];
	let monthCursor = today.startOf('month');
	const lastMonth = horizonEnd.startOf('month');
	while (monthCursor <= lastMonth) {
		months.push(toLocalJsDate(monthCursor));
		monthCursor = monthCursor.plus({ months: 1 });
	}

	const selectedJs = value
		? toLocalJsDate(DateTime.fromFormat(value, DATESTR_FMT, { zone }))
		: undefined;

	const handleSelect = (date?: Date) => {
		if (!date) {
			return;
		}
		const luxon = fromLocalJsDate(date, restaurant?.timezone);
		const dateStr = luxon.toFormat(DATESTR_FMT);
		analytics.track(FILTER_CHANGED, {
			filterName: 'date',
			filterValue: dateStr,
			source: 'open_parameters_calendar',
			...filterCtx
		});
		onChange(dateStr);
	};

	const lang = i18n.language === 'fr' ? 'fr' : 'en';

	return (
		<div className="max-h-[420px] overflow-y-auto overscroll-contain pr-1">
			<div className="flex flex-col gap-10">
				{months.map((month, idx) => (
					<Calendar
						key={idx}
						mode="single"
						month={month}
						selected={selectedJs}
						onSelect={handleSelect}
						hideNavigation
						disableNavigation
						showOutsideDays={false}
						classNames={{
							root: 'w-full !p-0',
							nav: 'hidden',
							button_previous: 'hidden',
							button_next: 'hidden',
							month_caption: 'flex w-full items-center justify-center',
							day: 'bg-tablz-med-gray-2 border border-tablz-med-gray-4 rounded-xl box-border w-full aspect-square flex items-center justify-center hover:bg-tablz-med-gray-6',
							day_button: 'appearance-none text-tablz-text-white-1 font-poppins font-semibold text-base !ring-0',
							week: 'grid grid-cols-7 mt-0 gap-2',
							weeks: 'flex flex-col gap-2',
							weekdays: 'my-3 grid grid-cols-7 gap-2',
							weekday: 'w-full text-center text-tablz-text-gray-1 font-poppins text-base font-medium',
							caption_label: 'text-tablz-text-white-2 font-poppins text-base font-semibold my-4 capitalize',
							selected: '!rounded-xl border-2 !border-tablz-dark-gray-1 bg-tablz-med-gray-6',
							disabled: cn('border-tablz-med-gray-1 bg-tablz-med-gray-5 !cursor-default hover:bg-tablz-med-gray-5')
						}}
						disabled={[
							{ before: toLocalJsDate(todayStart) },
							{ after: toLocalJsDate(horizonEnd) }
						]}
						formatters={{
							formatWeekdayName: (d) =>
								d.toLocaleString(lang, { weekday: 'short' }).charAt(0).toUpperCase(),
							formatCaption: (d) =>
								d.toLocaleString(lang, { month: 'long', year: 'numeric' })
						}}
					/>
				))}
			</div>
		</div>
	);
};
